import React, { useState, useRef, useEffect, useCallback } from 'react';
import './AutocompleteInput.css';

export interface AutocompleteSuggestion {
  name: string;
  pricePerUnit?: number;
  unit?: string;
  typeOfWorkId?: number;
}

interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[] | AutocompleteSuggestion[];
  placeholder?: string;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSuggestionSelect?: (suggestion: AutocompleteSuggestion) => void;
  className?: string;
  maxSuggestions?: number;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  suggestions,
  placeholder,
  autoFocus,
  onKeyDown,
  onSuggestionSelect,
  className = '',
  maxSuggestions = 10
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Нормализация предложений (строки или объекты)
  const normalizedSuggestions = React.useMemo(() => {
    return suggestions.map(suggestion => {
      if (typeof suggestion === 'string') {
        return { name: suggestion };
      }
      return suggestion;
    });
  }, [suggestions]);

  // Фильтрация предложений
  const filteredSuggestions = normalizedSuggestions
    .filter(suggestion => {
      const name = suggestion.name.toLowerCase();
      const searchValue = value.toLowerCase();
      return name.includes(searchValue) && name !== searchValue;
    })
    .slice(0, maxSuggestions);

  // Открываем список при фокусе и наличии предложений
  useEffect(() => {
    if (value && filteredSuggestions.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [value, filteredSuggestions.length]);

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Проверяем, что клик не был внутри dropdown или input
      if (
        dropdownRef.current?.contains(target) ||
        inputRef.current?.contains(target)
      ) {
        return;
      }
      
      // Закрываем только если клик действительно был вне компонента
      setIsOpen(false);
    };

    if (isOpen) {
      // Используем capture фазу для правильного порядка обработки
      document.addEventListener('mousedown', handleClickOutside, true);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isOpen]);

  // Обработка выбора предложения
  const handleSelectSuggestion = useCallback((suggestion: AutocompleteSuggestion) => {
    onChange(suggestion.name);
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    }
    setIsOpen(false);
    setSelectedIndex(-1);
  }, [onChange, onSuggestionSelect]);

  // Обработка клавиатуры
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredSuggestions.length === 0) {
      if (onKeyDown) onKeyDown(e);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
          e.preventDefault();
          handleSelectSuggestion(filteredSuggestions[selectedIndex]);
        } else if (onKeyDown) {
          onKeyDown(e);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        if (onKeyDown) onKeyDown(e);
        break;
      default:
        if (onKeyDown) onKeyDown(e);
    }
  };

  // Прокрутка к выбранному элементу
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [selectedIndex]);

  return (
    <div 
      className="autocomplete-input-wrapper"
      onClick={(e) => {
        // Позволяем клики на input, но блокируем всплытие для родительских элементов
        e.stopPropagation();
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (filteredSuggestions.length > 0) {
            setIsOpen(true);
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={`autocomplete-input ${className}`}
        autoComplete="off"
      />
      
      {isOpen && filteredSuggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="autocomplete-dropdown"
          onMouseDown={(e) => {
            // Предотвращаем всплытие, чтобы клик не проходил сквозь
            e.stopPropagation();
          }}
          onClick={(e) => {
            // Останавливаем всплытие клика
            e.stopPropagation();
          }}
        >
          {filteredSuggestions.map((suggestion, index) => {
            // Подсветка совпадающей части
            const suggestionName = suggestion.name;
            const lowerSuggestion = suggestionName.toLowerCase();
            const lowerValue = value.toLowerCase();
            const matchIndex = lowerSuggestion.indexOf(lowerValue);
            
            let displayText;
            if (matchIndex >= 0) {
              const before = suggestionName.substring(0, matchIndex);
              const match = suggestionName.substring(matchIndex, matchIndex + value.length);
              const after = suggestionName.substring(matchIndex + value.length);
              
              displayText = (
                <>
                  {before}
                  <span className="autocomplete-highlight">{match}</span>
                  {after}
                </>
              );
            } else {
              displayText = suggestionName;
            }

            // Дополнительная информация (цена и единица измерения)
            const hasAdditionalInfo = suggestion.pricePerUnit !== undefined || suggestion.unit;
            const additionalInfo = hasAdditionalInfo ? (
              <span className="autocomplete-suggestion-info">
                {suggestion.unit && <span className="autocomplete-unit">{suggestion.unit}</span>}
                {suggestion.pricePerUnit !== undefined && (
                  <span className="autocomplete-price">{suggestion.pricePerUnit.toLocaleString('ru-RU')} ₽</span>
                )}
              </span>
            ) : null;

            return (
              <div
                key={`${suggestionName}-${index}`}
                className={`autocomplete-item ${
                  index === selectedIndex ? 'selected' : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectSuggestion(suggestion);
                }}
                onMouseDown={(e) => {
                  // Останавливаем всплытие, чтобы клик не проходил сквозь
                  e.stopPropagation();
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <i className="ri-text"></i>
                <div className="autocomplete-suggestion-content">
                  <span>{displayText}</span>
                  {additionalInfo}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
